import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

// Blocked words list for content filtering
const blockedWords = ['neger', 'negrer', 'negern', 'negrerna'];

function containsBlockedWord(text: string): boolean {
  const lowerText = text.toLowerCase();
  return blockedWords.some(word => lowerText.includes(word));
}

function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export type SortOption = "relevance" | "alphabetical" | "match";

export interface QuestionSearchResult {
  word: string;
  wordLength: number;
  clues: string[];
  matchPercentage: number;
  language: number;
}

export interface WordSearchResult {
  word: string;
  language: number;
}

export const useWordSearch = () => {
  const { isAdmin } = useAuth();
  const isAdminRef = useRef(isAdmin);
  
  useEffect(() => {
    isAdminRef.current = isAdmin;
  }, [isAdmin]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<WordSearchResult[]>([]);
  const [questionResults, setQuestionResults] = useState<QuestionSearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<SortOption>("relevance");
  const [selectedLength, setSelectedLength] = useState<number | null>(null);

  const calculateMatchPercentage = (clue: string, searchTerm: string, wordLength: number, targetLength: number | null): number => {
    const clueUpper = clue.toUpperCase().replace(/[^A-ZÅÄÖ]/g, '');
    const searchUpper = searchTerm.toUpperCase().replace(/[^A-ZÅÄÖ]/g, '');
    
    if (!clueUpper || !searchUpper) return 0;
    
    // Base percentage from text matching
    let basePercentage = 0;
    
    // If exact match, return 100%
    if (clueUpper === searchUpper) {
      basePercentage = 100;
    } else {
      // Split search into words for partial matching
      const searchWords = searchTerm.toUpperCase().split(/\s+/).filter(w => w.length > 0);
      
      // Calculate how much of the clue is covered by search terms
      let matchedChars = 0;
      
      for (const searchWord of searchWords) {
        const cleanSearchWord = searchWord.replace(/[^A-ZÅÄÖ]/g, '');
        if (cleanSearchWord && clueUpper.includes(cleanSearchWord)) {
          matchedChars += cleanSearchWord.length;
        }
      }
      
      // Return percentage based on clue coverage (how much of clue matches)
      basePercentage = Math.round((matchedChars / clueUpper.length) * 100);
      basePercentage = Math.min(basePercentage, 100);
    }
    
    // Apply length penalty if a target length is selected
    if (targetLength !== null && wordLength !== targetLength) {
      // Reduce match percentage by 30% for wrong length
      basePercentage = Math.round(basePercentage * 0.7);
    }
    
    return basePercentage;
  };

  const sortResults = useCallback((results: QuestionSearchResult[], option: SortOption): QuestionSearchResult[] => {
    const sorted = [...results];
    switch (option) {
      case "alphabetical":
        return sorted.sort((a, b) => a.word.localeCompare(b.word, 'sv'));
      case "match":
        return sorted.sort((a, b) => b.matchPercentage - a.matchPercentage);
      case "relevance":
      default:
        // Sort by match percentage first, then by number of clues (more clues = more common word), then alphabetically
        return sorted.sort((a, b) => {
          if (b.matchPercentage !== a.matchPercentage) {
            return b.matchPercentage - a.matchPercentage;
          }
          // More clues = more common/important word
          if (b.clues.length !== a.clues.length) {
            return b.clues.length - a.clues.length;
          }
          return a.word.localeCompare(b.word, 'sv');
        });
    }
  }, []);

  const searchWords = useCallback(async (pattern: string, searchType: "word" | "question", lengthFilter: number | null = null) => {
    setIsLoading(true);
    setError(null);
    setResults([]);
    setQuestionResults([]);

    try {
      // Replace ? with _ for SQL LIKE and * with % for wildcard
      const upperPattern = pattern.toUpperCase().replace(/\?/g, '_').replace(/\*/g, '%');

      if (searchType === "word") {
        // Search for words matching the pattern
        let query = supabase
          .from('words')
          .select('word, length, language')
          .ilike('word_upper', upperPattern)
          .limit(500);

        // Apply length filter if specified
        if (lengthFilter !== null) {
          query = query.eq('length', lengthFilter);
        }

        const { data, error: dbError } = await query;

        if (dbError) throw dbError;

        const searchResults: WordSearchResult[] = (data || [])
          .filter(row => !containsBlockedWord(row.word))
          .map(row => ({
            word: capitalize(row.word),
            language: row.language ?? 1
          }));

        // Sort results alphabetically
        searchResults.sort((a, b) => a.word.localeCompare(b.word, 'sv'));
        setResults(searchResults);

        // Log search for statistics (fire and forget) - skip if admin
        if (!isAdminRef.current) {
          supabase.from('search_logs').insert({
            search_term: pattern,
            search_type: 'word',
            results_count: searchResults.length,
            selected_length: lengthFilter,
          }).then(() => {});
        }

      } else {
        // Search for clues matching the pattern, return associated words with clue info
        const { data, error: dbError } = await supabase
          .from('clues')
          .select(`
            clue,
            words!inner(word, length, language)
          `)
          .ilike('clue_upper', `%${upperPattern}%`)
          .limit(500);

        if (dbError) throw dbError;

        // Group results by word, collecting all matching clues
        const wordMap = new Map<string, { clues: string[], maxMatchPercentage: number, wordLength: number, language: number }>();
        
        (data || []).forEach(row => {
          const word = (row.words as any)?.word;
          const wordLength = (row.words as any)?.length || word?.length || 0;
          const language = (row.words as any)?.language ?? 1;
          if (word && !containsBlockedWord(word) && !containsBlockedWord(row.clue)) {
            const capitalizedWord = capitalize(word);
            const matchPercentage = calculateMatchPercentage(row.clue, pattern, wordLength, lengthFilter);
            
            const existing = wordMap.get(capitalizedWord);
            if (existing) {
              existing.clues.push(row.clue);
              existing.maxMatchPercentage = Math.max(existing.maxMatchPercentage, matchPercentage);
            } else {
              wordMap.set(capitalizedWord, { clues: [row.clue], maxMatchPercentage: matchPercentage, wordLength, language });
            }
          }
        });

        // Convert to array format
        const detailedResults: QuestionSearchResult[] = Array.from(wordMap.entries()).map(([word, data]) => ({
          word,
          wordLength: data.wordLength,
          clues: data.clues,
          matchPercentage: data.maxMatchPercentage,
          language: data.language
        }));

        // Sort by current sort option
        const sortedResults = sortResults(detailedResults, sortOption);
        setQuestionResults(sortedResults);
        
        // Also set simple results for backward compatibility
        const simpleResults: WordSearchResult[] = sortedResults.map(r => ({ word: r.word, language: r.language }));
        setResults(simpleResults);

        // Log search for statistics (fire and forget) - skip if admin
        if (!isAdminRef.current) {
          supabase.from('search_logs').insert({
            search_term: pattern,
            search_type: 'question',
            results_count: simpleResults.length,
            selected_length: lengthFilter,
          }).then(() => {});
        }
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ett fel uppstod';
      setError(errorMessage);
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [sortOption, sortResults]);

  const changeSortOption = useCallback((option: SortOption) => {
    setSortOption(option);
    if (questionResults.length > 0) {
      const sorted = sortResults(questionResults, option);
      setQuestionResults(sorted);
    }
  }, [questionResults, sortResults]);

  const clearResults = useCallback(() => {
    setResults([]);
    setQuestionResults([]);
    setError(null);
  }, []);

  // Helper to get simple word array for backward compatibility
  const getSimpleResults = useCallback(() => {
    return results.map(r => r.word);
  }, [results]);

  return { 
    searchWords, 
    results, 
    questionResults,
    isLoading, 
    error, 
    clearResults,
    sortOption,
    changeSortOption,
    selectedLength,
    setSelectedLength
  };
};
